import { MedplumClient, tryGetJwtExpiration } from '@medplum/core'
import { MimeType } from 'constants/mimetype'
import { useStore } from 'store'
import { ALLOWED_STATUS_CODES, medplum } from './medplumClient'
import { captureException, SentrySources, TransactionNames } from './sentry'

/**
 * Helper to get session state from store safely.
 * Returns undefined if store is not available (e.g., in tests without proper mock).
 */
const getSessionState = ():
  | {
      isFeatureEnabled: boolean
      setExpired: (v: boolean) => void
    }
  | undefined => {
  try {
    return useStore.getState()?.session
  } catch {
    return undefined
  }
}

/**
 * Helper to set session as expired in the store.
 * Uses a closure flag to prevent duplicate state updates.
 * Only sets expired if the session expired modal feature flag is enabled.
 * Returns true if session was set to expired, false otherwise.
 */
const setSessionExpired = ((): (() => boolean) => {
  let expired = false
  return (): boolean => {
    const session = getSessionState()
    if (!session) return false
    const { isFeatureEnabled, setExpired } = session
    if (!expired && isFeatureEnabled) {
      expired = true
      setExpired(true)
      return true
    }
    return expired && isFeatureEnabled
  }
})()

export class HttpResponseError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    Object.setPrototypeOf(this, HttpResponseError.prototype)
    this.name = 'HttpResponseError'
  }
}

export interface HttpClientFetchOptions extends RequestInit {
  /** Status codes that are expected for this request; logged locally, not sent to Sentry. */
  expectedStatusCodes?: number[]
}

export class HttpClient {
  private medplum: MedplumClient
  private accessToken: string | undefined

  constructor() {
    this.medplum = medplum
    this.accessToken = this.medplum.getAccessToken()
    if (!this.accessToken) {
      throw new Error('Access token is required')
    }
  }

  async fetch(url: string, options?: HttpClientFetchOptions): Promise<Response> {
    const { expectedStatusCodes = [], ...fetchOptions } = options ?? {}
    const { fetch: originalFetch } = window

    // Proactive token expiration check before making the request (only if feature flag enabled)
    // Note: accessToken is validated in constructor, so it will always be defined here
    if (getSessionState()?.isFeatureEnabled) {
      const expiration = this.accessToken ? tryGetJwtExpiration(this.accessToken) : undefined
      if (expiration && Date.now() >= expiration) {
        setSessionExpired()
        // Return a mock 401 response to prevent downstream errors
        return new Response(null, { status: 401, statusText: 'Token Expired' })
      }
    }

    const headers = {
      ...fetchOptions?.headers,
      'Content-Type': fetchOptions?.headers?.['Content-Type']
        ? fetchOptions?.headers?.['Content-Type']
        : MimeType.ApplicationJSON,
      Authorization: `Bearer ${this.accessToken}`
    }

    let response

    try {
      response = await originalFetch(url, {
        ...fetchOptions,
        headers
      })

      // Handle 401 responses - set session expired and return (only if feature flag enabled)
      if (response.status === 401 && setSessionExpired()) {
        return new Promise<Response>(() => {
          // Never resolves - app is frozen until user refreshes via modal
        })
      }

      const status = Number(response.status)
      if (response?.status && !ALLOWED_STATUS_CODES.includes(status)) {
        const errorMessage = `Failed to fetch ${url}: got status ${response.status}`
        if (expectedStatusCodes.includes(status)) {
          // eslint-disable-next-line no-console
          console.error(errorMessage)
        } else {
          captureException(new Error(errorMessage), {
            tags: { source: SentrySources.FASTAPI_FETCH_MIDDLEWARE, url },
            extras: { params: fetchOptions, info: response },
            transactionName: TransactionNames.FETCH
          })
        }
      }

      return response
    } catch (error) {
      const isLoadFailedError = error instanceof TypeError && error.message.includes('Load failed')

      if (error instanceof TypeError && !navigator.onLine) {
        // eslint-disable-next-line no-console
        console.error('Network error: offline', error)
      } else if (isLoadFailedError) {
        // eslint-disable-next-line no-console
        console.error('Network error: Load failed', error)
      } else {
        captureException(error, {
          tags: { source: SentrySources.FASTAPI_FETCH_MIDDLEWARE, url },
          extras: { params: fetchOptions, errors: [error] },
          transactionName: TransactionNames.FETCH
        })
      }
      throw error
    }
  }
}
